import { useEffect } from 'preact/hooks';
import { useMe, challengeUrl } from '../auth/useMe';
import { Layout } from '../components/Layout';
import { PageContainer, Eyebrow } from '../primitives';

/**
 * <c>/me</c> resolver. Bounces an authenticated requester to their
 * own <c>/u/{slug}</c> profile, sends anonymous visitors to sign-in
 * with a return path, and surfaces a "no slug yet" hint for the
 * unlikely case of a missing slug claim.
 *
 * <p>Per ADR-0015 + legacy ADR-0030 the user-facing entry to their
 * own surfaces is <c>/me</c>; the public profile lives at
 * <c>/u/{slug}</c>. Splitting the entry from the canonical URL means
 * the navbar can link <c>/me</c> unconditionally and the redirect
 * sorts the rest out.</p>
 */
export function MeRedirectPage() {
    const me = useMe();

    useEffect(() => {
        if (typeof window === 'undefined') return;
        if (me.status === 'authenticated') {
            const slug = me.user?.claims?.slug?.[0];
            if (slug) {
                window.location.replace(`/u/${encodeURIComponent(slug)}`);
            }
            // No slug - render the hint below; no redirect.
            return;
        }
        if (me.status === 'anonymous') {
            window.location.replace(challengeUrl('discord', '/me'));
        }
    }, [me.status, me.status === 'authenticated' ? me.user?.claims?.slug?.[0] : null]);

    return (
        <Layout>
            <PageContainer>
                <div class="py-16 text-center">
                    <Eyebrow>Your profile</Eyebrow>
                    {me.status === 'loading' && (
                        <p class="mt-4 text-sm text-fg-muted">Resolving your account…</p>
                    )}
                    {me.status === 'anonymous' && (
                        <p class="mt-4 text-sm text-fg-muted">Sending you to sign-in…</p>
                    )}
                    {me.status === 'authenticated' &&
                        !me.user?.claims?.slug?.[0] && (
                            <>
                                <h1 class="mt-2 font-display text-3xl">No handle yet</h1>
                                <p class="mt-4 max-w-[42rem] text-sm text-fg-muted">
                                    Your account has no slug assigned. Open
                                    account settings to pick one - your public
                                    profile lives at{' '}
                                    <code class="font-mono">/u/your-slug</code>.
                                </p>
                                <a
                                    href="/me/account"
                                    class="mt-6 inline-flex rounded border border-accent bg-accent px-4 py-2 text-sm text-surface-base hover:bg-fg-primary"
                                >
                                    Open account settings
                                </a>
                            </>
                        )}
                </div>
            </PageContainer>
        </Layout>
    );
}
